// Kit d'interface commun aux pages Agent autonome, Template et Surfer.
// Couleurs neutres et douces ; l'accent suit la couleur du thème courant
// (celle de la PV sélectionnée), toujours en teinte légère.
import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useThemeColor } from '../../components/Theme/ThemeColorContext';

export const FONT = "'Poppins', sans-serif";

export const NEUTRAL = {
    ink: '#1F1D1A',
    text: '#3D3A33',
    muted: '#76705F',
    faint: '#A59F8F',
    line: 'rgba(31, 29, 26, 0.08)',
    lineStrong: 'rgba(31, 29, 26, 0.14)',
    surface: '#FFFFFF',
    subtle: '#F7F6F2',
    success: '#2E9E5B',
    warning: '#C98A0B',
    danger: '#C2410C',
} as const;

const FALLBACK_THEME = '#6B7A8F';

/** Couleur du thème ; hors fournisseur (rendu isolé, tests), une couleur neutre */
export const useSoftTheme = (): string => {
    try {
        return useThemeColor().currentThemeColor || FALLBACK_THEME;
    } catch {
        return FALLBACK_THEME;
    }
};

/** Teinte transparente d'une couleur, quel que soit son format (hex 3, 6 ou 8 chiffres, rgb) */
export const tint = (color: string, amount: number): string => {
    try {
        return alpha(color, amount);
    } catch {
        return alpha(FALLBACK_THEME, amount);
    }
};

export const cardSx = {
    backgroundColor: NEUTRAL.surface,
    borderRadius: '16px',
    border: `1px solid ${NEUTRAL.line}`,
    boxShadow: '0 1px 2px rgba(31, 29, 26, 0.04), 0 8px 24px rgba(31, 29, 26, 0.04)',
    textAlign: 'left',
} as const;

export const primaryButtonSx = (theme: string) => ({
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 14,
    fontFamily: FONT,
    height: 42,
    px: 2.5,
    borderRadius: '10px',
    color: '#fff',
    backgroundColor: theme,
    boxShadow: `0 4px 12px ${tint(theme, 0.22)}`,
    '&:hover': { backgroundColor: theme, filter: 'brightness(0.94)', boxShadow: `0 6px 16px ${tint(theme, 0.28)}` },
    '&.Mui-disabled': { backgroundColor: NEUTRAL.subtle, color: NEUTRAL.faint, boxShadow: 'none' },
});

export const secondaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 14,
    fontFamily: FONT,
    height: 42,
    px: 2.25,
    borderRadius: '10px',
    color: NEUTRAL.text,
    backgroundColor: NEUTRAL.surface,
    border: `1px solid ${NEUTRAL.lineStrong}`,
    '&:hover': { backgroundColor: NEUTRAL.subtle, borderColor: NEUTRAL.lineStrong },
    '&.Mui-disabled': { color: NEUTRAL.faint, borderColor: NEUTRAL.line },
} as const;

/** Champs MUI : coins arrondis et focus à la couleur du thème */
export const fieldSx = (theme: string) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: NEUTRAL.surface,
        fontFamily: FONT,
        '& fieldset': { borderColor: NEUTRAL.lineStrong },
        '&:hover fieldset': { borderColor: tint(theme, 0.45) },
        '&.Mui-focused fieldset': { borderColor: theme, borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: theme },
});

/** Pastille d'icône teintée */
export const IconBadge: React.FC<{ theme: string; children: React.ReactNode; size?: number }> = ({ theme, children, size = 40 }) => (
    <Box
        sx={{
            width: size, height: size, borderRadius: '12px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: tint(theme, 0.1), color: theme,
            '& svg': { fontSize: size * 0.5 },
        }}
    >
        {children}
    </Box>
);

/** Carte avec en-tête (icône, titre, description, actions) */
export const SectionCard: React.FC<{
    theme: string;
    icon?: React.ReactNode;
    title: string;
    description?: React.ReactNode;
    actions?: React.ReactNode;
    children?: React.ReactNode;
    sx?: object;
    bodySx?: object;
    testId?: string;
}> = ({ theme, icon, title, description, actions, children, sx, bodySx, testId }) => (
    <Box data-testid={testId} sx={{ ...cardSx, display: 'flex', flexDirection: 'column', minWidth: 0, ...sx }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 2, borderBottom: children ? `1px solid ${NEUTRAL.line}` : 'none' }}>
            {icon && <IconBadge theme={theme}>{icon}</IconBadge>}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 15.5, fontWeight: 600, color: NEUTRAL.ink, fontFamily: FONT, lineHeight: 1.35 }}>{title}</Typography>
                {description && (
                    <Typography component="div" sx={{ fontSize: 13, color: NEUTRAL.muted, mt: 0.25, fontFamily: FONT, lineHeight: 1.5 }}>{description}</Typography>
                )}
            </Box>
            {actions && <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}>{actions}</Box>}
        </Box>
        {children && <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2, flex: 1, ...bodySx }}>{children}</Box>}
    </Box>
);

/** Pastille d'état : point coloré et libellé */
export const StatusPill: React.FC<{ color: string; label: string; testId?: string }> = ({ color, label, testId }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: '999px', backgroundColor: tint(color, 0.1) }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color }} />
        <Typography data-testid={testId} sx={{ fontSize: 12.5, fontWeight: 600, color: NEUTRAL.text, fontFamily: FONT }}>{label}</Typography>
    </Box>
);
